import { Controller } from "@hotwired/stimulus"

/**
 * Gates the "add dealer" form on a customer number the server has actually approved.
 *
 * The screen has one field and one decision behind it: can this number be added as a new
 * account. So the check is not an optional extra beside the field - it IS the form. The
 * submit button starts locked and is unlocked only by an answer, which removes the two
 * things that made the old version confusing: a Check button that did what leaving the
 * field already did, and a Continue button that would happily submit a number the check
 * had just rejected.
 *
 * It points a turbo frame at the check URL and lets the server render the answer. No
 * fetch, no JSON, and no user-facing copy in here - the frame's contents come from ERB,
 * which is why i18n-tasks is configured to ignore JS paths.
 *
 * The verdict itself is not computed here. The rendered answer carries one boolean,
 * data-number-addable, decided in Ruby by the same object the POST consults - so what the
 * button offers and what the server accepts cannot drift apart. It also carries the value
 * it was an answer about, because an answer to "TPR0" says nothing about "TPR02", and the
 * two arrive milliseconds apart while somebody is typing.
 *
 * Checks on a debounce while typing and immediately on blur, so the answer is usually
 * already there by the time the eye reaches the button. Enter runs the check rather than
 * submitting until there is something to submit.
 *
 * Expected markup:
 *   <div data-controller="customer-number-check"
 *        data-customer-number-check-url-value="/…/number-check">
 *     <input data-customer-number-check-target="input"
 *            data-action="input->customer-number-check#typed
 *                         blur->customer-number-check#check
 *                         keydown.enter->customer-number-check#enter">
 *     <span data-customer-number-check-target="pending" class="d-none">Checking…</span>
 *     <turbo-frame id="customer-number-check" data-customer-number-check-target="frame">
 *       …[data-number-addable="true"][data-number-checked="TPR02"], rendered server-side
 *     <input type="submit" data-customer-number-check-target="submit">
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "frame", "submit", "pending"]
  static values = { url: String, debounce: { type: Number, default: 350 } }

  connect() {
    this.timer = null
    this.lastChecked = null
    // Turbo fires this on the frame once the check has rendered into it.
    this.onFrameLoad = () => this.applyVerdict()
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener("turbo:frame-load", this.onFrameLoad)
    }
    // Whatever the server painted is the starting verdict. On a fresh form that is
    // "nothing typed" and the button locks; on the re-render after a refused POST it is
    // the refusal, which is what should still be holding the button shut.
    this.applyVerdict()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    if (this.hasFrameTarget) {
      this.frameTarget.removeEventListener("turbo:frame-load", this.onFrameLoad)
    }
  }

  // Every keystroke invalidates the answer on screen: the button locks immediately and
  // stays locked until a check about the new value comes back. Waiting for the debounce
  // to lock it would leave a third of a second in which the button says yes about a
  // number nobody has checked.
  typed() {
    this.lock()
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.check(), this.debounceValue)
  }

  // Enter submits only once there is something to submit. Before that it asks - which is
  // the request that was being made by pressing it anyway.
  enter(event) {
    if (this.unlocked()) return

    event.preventDefault()
    this.check()
  }

  check() {
    if (this.timer) clearTimeout(this.timer)
    if (!this.hasInputTarget || !this.hasFrameTarget) return

    const number = this.currentNumber()
    if (number === this.lastChecked) return

    this.lastChecked = number
    this.lock()
    this.show(this.pending)

    // Setting src to what it already is loads nothing, which would leave the retry
    // spinning forever. Asking the frame to reload is the same request, made again.
    const url = this.checkUrl(number)
    if (this.frameTarget.getAttribute("src") === url && typeof this.frameTarget.reload === "function") {
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = url
    }
  }

  // Wired to the retry the frame renders when Rubicon did not answer. That is the one
  // state where asking the same question again can produce a different answer.
  recheck() {
    this.lastChecked = null
    this.check()
  }

  checkUrl(number) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("customer_number", number)
    return url.toString()
  }

  applyVerdict() {
    this.hide(this.pending)

    const verdict = this.hasFrameTarget ? this.frameTarget.querySelector("[data-number-addable]") : null
    if (!verdict) return this.lock()

    // An answer about a value that has since been typed over decides nothing. Without
    // this, a slow response to "TPR0" could unlock the button under "TPR02".
    if (verdict.dataset.numberChecked !== this.currentNumber()) return this.lock()

    if (verdict.dataset.numberAddable === "true") this.unlock()
    else this.lock()
  }

  get pending() {
    return this.hasPendingTarget ? this.pendingTarget : null
  }

  currentNumber() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : ""
  }

  unlocked() {
    return this.hasSubmitTarget && !this.submitTarget.disabled
  }

  lock() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
  }

  unlock() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = false
  }

  show(element) {
    if (element) element.classList.remove("d-none")
  }

  hide(element) {
    if (element) element.classList.add("d-none")
  }
}
